import { useQuery } from '@apollo/client';
import { isEmpty } from 'lodash';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import shortid from 'shortid';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import useKeyDownListener from 'hooks/useKeyDownListener';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Header from 'layout/atoms/Header/Header';
import Input from 'layout/atoms/Input/Input';
import InputError from 'layout/atoms/InputError/InputError';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal } from 'store/layoutSlice';
import { addSize, fetchSizes, resetSizes } from 'store/parameters/sizesSlice';
import useSizeCreatorValidation from './useSizeCreatorValidation';
function SizeCreator() {
    var _a, _b;
    const { t } = useTranslation(['specification', 'common']);
    const { data: tenant } = useQuery(GET_CURRENT_TENANT);
    const tenantId = (_b = (_a = tenant === null || tenant === void 0 ? void 0 : tenant.me) === null || _a === void 0 ? void 0 : _a.currentTenant) === null || _b === void 0 ? void 0 : _b.id;
    const dispatch = useDispatch();
    const [sizeStandard, setSizeStandard] = useState('');
    const [sizeNames, setSizeNames] = useState([]);
    const [sizeNameInput, setSizeNameInput] = useState('');
    const MAX_SIZES_COUNT = 7;
    const sizeNameInputError = sizeNames.some((sizeName) => (sizeName === null || sizeName === void 0 ? void 0 : sizeName.name) === sizeNameInput) && !isEmpty(sizeNameInput);
    const maxSizesReached = sizeNames.length >= MAX_SIZES_COUNT;
    const addElementDisabled = isEmpty(sizeNameInput) || sizeNameInputError || maxSizesReached;
    const { errors, isValid, pendingFields, markTouched, validateAll } = useSizeCreatorValidation(sizeStandard, sizeNames, t);
    const handleSizeStandardNameChange = ({ target: { value } }) => {
        setSizeStandard(value);
    };
    const handleSizeChange = (id, value) => {
        setSizeNames((rest) => rest.map((sizeName) => {
            if (sizeName.id === id) {
                const isDuplicated = rest.some((restSizeName) => restSizeName.name === value);
                const isSizeNameEmpty = isEmpty(value);
                let errorMessage = '';
                if (isDuplicated) {
                    errorMessage = t('element_already_exists');
                }
                if (isSizeNameEmpty) {
                    errorMessage = t('element_cannot_be_empty');
                }
                // prioritize empty input error over duplication error
                return {
                    id,
                    name: value,
                    error: isDuplicated || isSizeNameEmpty,
                    errorMessage,
                };
            }
            return sizeName;
        }));
    };
    const handleSizeRemove = (id) => {
        setSizeNames((rest) => rest.filter((sizeName) => sizeName.id !== id));
    };
    const handleSizeAdd = () => {
        setSizeNames((rest) => [
            ...rest,
            { id: shortid(), name: sizeNameInput, error: false, errorMessage: '' },
        ]);
        setSizeNameInput('');
    };
    const handleSizingAdd = () => {
        if (!validateAll())
            return;
        dispatch(addSize({
            tenantId,
            name: sizeStandard,
            sizeNames: sizeNames.map((sizeName) => sizeName.name),
        })).then(() => {
            // the mutation response doesn't include the freshly created size names, so refetch to get them
            dispatch(resetSizes());
            dispatch(fetchSizes());
        });
        dispatch(closeModal({ name: 'sizeCreator' }));
    };
    const handleSizeNameChange = ({ target: { value } }) => {
        setSizeNameInput(value);
    };
    const handleCancel = () => {
        dispatch(closeModal({ name: 'sizeCreator' }));
    };
    useKeyDownListener([
        { code: 'Enter', callback: handleSizingAdd },
        { code: 'Escape', callback: handleCancel },
    ]);
    return (React.createElement(Modal, { name: "sizeCreator", size: "s", header: React.createElement(Label, { weight: "strong" }, t('add_size')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: false, pendingFields: pendingFields, onSave: handleSizingAdd, onCancel: handleCancel, validateAll: validateAll }), onClose: handleCancel },
        React.createElement(Grid, { gap: 24 },
            React.createElement(FormField, { label: t('size_name_capitalized'), error: errors.name },
                React.createElement(Input, { value: sizeStandard, onChange: handleSizeStandardNameChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name) })),
            React.createElement(Grid, null,
                React.createElement(Header, { size: "m" }, t('sizes')),
                React.createElement(InputError, { message: errors.sizes }),
                sizeNames.map((sizeName) => (React.createElement("div", { key: sizeName.id, style: { display: 'flex', gap: '12px' } },
                    React.createElement(Input, { name: sizeName.id, value: sizeName.name, onChange: () => handleSizeChange(sizeName.id, sizeName.name) }),
                    React.createElement(Button, { theme: "border", shape: "squared", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => handleSizeRemove(sizeName.id) })))),
                React.createElement("div", null,
                    React.createElement("div", { style: { display: 'flex', gap: '12px' } },
                        React.createElement(Input, { name: "newSizeName", value: sizeNameInput, disabled: maxSizesReached, onChange: handleSizeNameChange }),
                        React.createElement(Button, { color: "primary", onClick: () => handleSizeAdd(), disabled: addElementDisabled }, t('common:add'))),
                    sizeNameInputError && React.createElement("h5", { className: "errorState" }, t('element_already_exists')),
                    maxSizesReached && React.createElement("h5", null, t('max_sizes_reached')))))));
}
export default memo(SizeCreator);
